'use client';

import {
  ArticleCard,
  AuthorByline,
  CardDate,
  CardTimeAgo,
  Eyebrow,
  Image,
  Title,
} from '@/app_components/ArticleSections/ArticleCards/ArticleCard';
import { Icon } from '@/app_components/common/Icon';
import { useContentCardTrackingData } from '@/app_lib/services/contentCardTracking';
import { getExtendedArticle } from '@/app_lib/services/contentCardTracking/utils';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import type { EventData } from '@/app_lib/types/Gtm';
import { cn, getPrimarySection } from '@/app_lib/utils/utils';

export const ArticleCardTimed = ({
  article,
  className,
  index = 1,
  priority,
  trackingData,
  /** @experiment recirc_layout_test — remove when experiment concludes */
  variant = 'timed',
}: {
  article: ArticlePreview;
  className?: string;
  priority?: boolean;
  trackingData: EventData;
  index?: number;
  /** @experiment recirc_layout_test — remove when experiment concludes */
  variant?: 'timed' | 'small';
}) => {
  const generateContentCardTrackingData = useContentCardTrackingData();
  const section = getPrimarySection(article);
  const tag = article.classification.primaryPostTag;
  const horizontal = variant === 'small';

  // TODO: @james_kurlander we want urls to sections to end in a slash, is this something we want to have in all article cards?
  // also TODO: check to see if we want it for tags too
  const sectionUrl = section.url.endsWith('/') ? section.url : `${section.url}/`;

  return (
    <ArticleCard
      className={cn(className, horizontal && 'flex-row')}
      variant='timed'
    >
      <Image
        className={horizontal ? 'w-[120px] md:w-[200px] shrink-0' : undefined}
        contentCardTrackingData={generateContentCardTrackingData({
          article: getExtendedArticle(article),
          cardPosition: index,
          clickTarget: 'image',
          moduleType: 'latest',
        })}
        image={article.image}
        imageAlt={article.imageAlt}
        priority={priority}
        trackingData={{ data: { ...trackingData, eventLabel: `title click : ${article.title}` } }}
        url={article.url}
        variant='timed'
      />
      <div className={cn('flex flex-col gap-sm', horizontal && 'flex-1')}>
        <div className={cn('flex items-center flex-wrap', section.name && tag.name && '-mb-1')}>
          {section.name && (
            <Eyebrow
              contentCardTrackingData={generateContentCardTrackingData({
                article: getExtendedArticle(article),
                cardPosition: index,
                clickTarget: 'eyebrow',
                moduleType: 'latest',
              })}
              sectionName={section.name}
              sectionUrl={sectionUrl}
              trackingData={{ data: { ...trackingData, eventLabel: `category click : ${section.name}` } }}
              variant={variant}
            />
          )}
          {section.name && tag.name && (
            <Icon
              className='text-red mb-1'
              icon='Dot'
              mode='both'
            />
          )}
          {tag.name && (
            <Eyebrow
              contentCardTrackingData={generateContentCardTrackingData({
                article: getExtendedArticle(article),
                cardPosition: index,
                clickTarget: 'eyebrow',
                moduleType: 'latest',
              })}
              sectionName={tag.name}
              sectionUrl={tag.url}
              trackingData={{ data: { ...trackingData, eventLabel: `category click : ${tag.name}` } }}
              variant={variant}
            />
          )}
        </div>
        <Title
          contentCardTrackingData={generateContentCardTrackingData({
            article: getExtendedArticle(article),
            cardPosition: index,
            clickTarget: 'headline',
            moduleType: 'latest',
          })}
          title={article.title}
          trackingData={{ data: { ...trackingData, eventLabel: `title click : ${article.title}` } }}
          url={article.url}
          variant={variant}
        />
        {horizontal && (
          <div className='flex flex-col'>
            <AuthorByline
              authors={article.authors}
              contentCardTrackingData={generateContentCardTrackingData({
                article: getExtendedArticle(article),
                cardPosition: index,
                clickTarget: 'author',
                moduleType: 'latest',
              })}
              trackingData={{ data: trackingData }}
              variant={variant}
            />
            <CardDate
              dateGmt={article.dateGmt}
              variant={variant}
            />
          </div>
        )}
      </div>
      {!horizontal && (
        <div className='flex flex-col'>
          <AuthorByline
            authors={article.authors}
            contentCardTrackingData={generateContentCardTrackingData({
              article: getExtendedArticle(article),
              cardPosition: index,
              clickTarget: 'author',
              moduleType: 'latest',
            })}
            trackingData={{ data: trackingData }}
            variant={variant}
          />
          <CardDate
            dateGmt={article.dateGmt}
            variant={variant}
          />
        </div>
      )}
      {!horizontal && (
        <CardTimeAgo
          dateGmt={article.dateGmt}
          variant={variant}
        />
      )}
    </ArticleCard>
  );
};
